import { useMemo } from 'react'
import * as React from 'react'
import useOfferList from 'hooks/Offers/useOfferList'
import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'LuxKit/Typography/Heading'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import {
  OfferListEventsProvider,
} from 'components/OfferList/OfferListEventsContext'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { EmptyObject } from 'lib/object/objectUtils'
import clsx from 'clsx'

import HeroOfferCarouselLookingForSomethingElse from 'home/components/LookingForSomethingElse/HeroOfferCarouselLookingForSomethingElse'
import PaletteSheet from 'LuxKit/PaletteSheet'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import getOfferListKey from 'lib/offer/offerListKey'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import { ANYWHERE_NAME, ANYWHERE_PLACE_ID } from 'constants/search'
import qs from 'qs'
import CarouselViewAllLink from 'components/Common/CarouselViewAllLink'
import type { LFSEOfferType } from 'home/components/LookingForSomethingElse/constants'
import { FormattedMessage, useIntl } from 'react-intl'
import useRebrandVersion from 'hooks/useRebrandVersion'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'
import { getExclusiveOfferCarouselSurface } from 'home/components/OfferCarousels/heroOfferCarouselSurface'

const ExclusiveOfferContainer = styled(PaletteSheet)`
  padding: ${rem(144)} 0 ${rem(32)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  &.burn-enabled {
    padding-top: ${rem(160)};
  }

  ${mediaQueryUp.tablet} {
    padding: ${rem(88)} 0 ${rem(40)};

    &.burn-enabled {
      padding-top: ${rem(88)};
    }
  }

  /*
   * Teal = Highlights / Primary / DarkBackground.
   * Dual gate must share useRebrandVersion() with ThemeProvider / body.app-palette-rebrand
   * (see HeroOfferCarouselSection) so OPTIMIZELY_* env override stays in sync.
   * Prefer &&.class over nested && under body… (nested && emits a broken selector).
   */
  body.${REBRAND_PALETTE_CLASS} &&.rebrand-highlight {
    background-color: var(--palette-highlight-primary-dark-background);
  }
`

const TopExclusiveHeaderGrid = styled.div`
  display: grid;
  align-items: center;
  grid-template-areas:
    "title link";
  grid-template-columns: 1fr max-content;
  grid-gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-gap: ${rem(16)};
  }

  > .title-area {
    grid-area: title;
  }

  > .link-area {
    grid-area: link;
  }
`

export const EXCLUSIVE_OFFER_CAROUSEL_LIMIT = 15

interface Props {
  filters?: App.OfferListFilters
  random?: boolean
  title?: React.ReactNode
  viewAllUrl?: string
  offerType?: LFSEOfferType
  burnEnabled?: boolean
  overrideOfferList?: App.OfferList
}

function HotelExclusiveOfferCarousel(props: Props) {
  const intl = useIntl()
  const {
    filters = EmptyObject,
    random,
    title = (
      <FormattedMessage
        id="l3wbcUEHDJMUgG_szQyX"
        defaultMessage="Today’s top <i>exclusive</i> offers"
        description="Top picks for me carousel title with italic 'exclusive'"
        values={{
          i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
        }}
      />
    ),
    viewAllUrl,
    offerType = 'hotels',
    burnEnabled,
    overrideOfferList,
  } = props
  const curatedOfferList = useOfferList(filters, { randomize: random })
  const offerList = overrideOfferList ?? curatedOfferList

  const tracking = useOfferListTracking(getListName('OfferList', 'Hotels', 'Hero Carousel'), { key: overrideOfferList?.key ?? getOfferListKey(filters) })

  const mapViewURL = useMemo(() => {
    if (viewAllUrl) {
      return viewAllUrl
    }
    const query = qs.stringify(
      {
        adults: 2,
        children: 'none',
        destinationId: ANYWHERE_PLACE_ID,
        destinationName: ANYWHERE_NAME,
        holidayTypes: 'Limited Time LUX Exclusive',
      },
    )
    return `/search?${query}`
  }, [viewAllUrl])

  const {
    isRebrandExclusiveSurface,
    containerPalette,
    titleSheetPalette,
    carouselSheetPalette,
    tileStyle,
  } = getExclusiveOfferCarouselSurface({
    isRebrand: useRebrandVersion() === 'rebrand',
  })

  return (
    <ExclusiveOfferContainer
      paletteType={containerPalette}
      className={clsx({
        'burn-enabled': burnEnabled,
        'rebrand-highlight': isRebrandExclusiveSurface,
      })}
    >
      <Sheet paletteType={titleSheetPalette} background="none">
        <Group direction="vertical" gap={24} tabletGap={32}>
          <LayoutContainer>
            <TopExclusiveHeaderGrid>
              <Heading className="title-area" variant="heading2" align="start">{title}</Heading>
              <CarouselViewAllLink className="link-area" to={mapViewURL}>
                {intl.formatMessage({
                  id: 'qywu0sy_1NVQHCWxPYIf',
                  defaultMessage: 'View all',
                  description: 'Button text to view all available items in the current category or listing section',
                })}
              </CarouselViewAllLink>
            </TopExclusiveHeaderGrid>
          </LayoutContainer>
          <Sheet paletteType={carouselSheetPalette} background="none">
            <OfferListEventsProvider tracking={tracking}>
              <OfferListCarousel
                tileStyle={tileStyle}
                tileSize="medium"
                overrideOfferList={offerList}
                limit={EXCLUSIVE_OFFER_CAROUSEL_LIMIT}
              >
                <HeroOfferCarouselLookingForSomethingElse
                  link={mapViewURL}
                  offerType={offerType}
                />
              </OfferListCarousel>
            </OfferListEventsProvider>
          </Sheet>
        </Group>
      </Sheet>
    </ExclusiveOfferContainer>
  )
}

export default HotelExclusiveOfferCarousel
